import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { fetchRollingReturnsPerformanceData } from '../../../repository/MutualFundDetail'
import { transformRollingReturnsPerformanceData } from '../../../utils/mutualFundDetail'
import { RollingReturnsPerformanceData } from '../../../types/mutual-fund-detail.types'
import ToggleBox from '../../ui-components/ToggleBox'
import RollingGraphLoader from '../../skeletons/RollingGraphLoader'
import GroupedBarChart from './RRGraph2'
import TextBubble from '../../ui-components/TextBubble'
import GraphUnavailable from '../../error-states/GraphUnavailable'
import { lightImpactVibrate, trackEvent } from '../../../utils/appBridge'
import { EventNames } from '../../../types/misc.types'
import HelpButton from '../../ui-components/HelpButton'
import VaulDrawer from '../../BottomSheetComponents/VaulDrawer'
import { RRBottomSheet } from '../../BottomSheetComponents/RRBottomSheet'
import { RRLearnMoreBottomSheet } from '../../BottomSheetComponents/RRLearnMoreBottomSheet'

interface DataPoint {
  x_value: number
  y_value: number
  label: number
}

type BenchmarkToggle = {
  name: string
  type: string
  value: string
}

type FundToggle = Omit<BenchmarkToggle, 'type' | 'value'>

const ROLLING_PERFORMANCE_SCREEN_NAME = `MF_L2_Rolling_Returns_Performance`

export default function RollingReturnsPerformanceSection() {
  const [openBottomSheet, setOpenBottomSheet] = useState(false)
  const { isin, data: metadata } = useMetaDataContext()
  const [data1, setData1] = useState<DataPoint[]>([])
  const [data2, setData2] = useState<DataPoint[]>([])
  const [currentFundtoggle, setCurrentFundToggle] = useState<FundToggle>({
    name: 'This fund'
  })
  const [currentBenchmarktoggle, setCurrentBenchmarkToggle] = useState<BenchmarkToggle>({
    name: metadata?.benchmarkFunds?.[0]?.name ?? '',
    type: metadata?.benchmarkFunds?.[0]?.type ?? '',
    value: metadata?.benchmarkFunds?.[0]?.value ?? ''
  })
  const [bottomSheetContentKey, setBottomSheetContentKey] = useState('')

  const { data, isLoading, isFetching, isError } = useQuery<any, Error>({
    queryKey: ['rollingReturnsPerformanceData', isin, currentBenchmarktoggle?.type, currentBenchmarktoggle?.value],
    queryFn: () => {
      return fetchRollingReturnsPerformanceData({
        isin,
        benchmarkType: currentBenchmarktoggle?.type,
        benchmarkValue: currentBenchmarktoggle?.value
      })
    },
    retry: 1,
    refetchOnWindowFocus: false
  })

  const renderBottomSheetContent = () => {
    switch (bottomSheetContentKey) {
      case 'rrContent':
        return (
          <RRBottomSheet
            onDismiss={() => setOpenBottomSheet((open) => !open)}
            ScreenName={ROLLING_PERFORMANCE_SCREEN_NAME}
          />
        )
      case 'learnMore':
        return (
          <RRLearnMoreBottomSheet
            onDismiss={() => setOpenBottomSheet((open) => !open)}
            ScreenName={ROLLING_PERFORMANCE_SCREEN_NAME}
          />
        )
      default:
        return null
    }
  }

  useEffect(() => {
    if (data) {
      const dataCopy: RollingReturnsPerformanceData = data?.data
      const transformedData = transformRollingReturnsPerformanceData({
        currFunds: dataCopy?.currFunds,
        benchmarkFunds: dataCopy?.benchmarkFunds
      })
      setData1(() => transformedData?.data1?.sort((a, b) => (a?.x_value ?? 0) - (b?.x_value ?? 0)))
      setData2(() => transformedData?.data2?.sort((a, b) => (a?.x_value ?? 0) - (b.x_value ?? 0)))
      setCurrentFundToggle(() => {
        return {
          toggle: 'This Fund'
        }
      })
    }
  }, [data])

  if (isLoading || isFetching)
    return (
      <div className='w-full px-2 h-max'>
        <RollingGraphLoader />
      </div>
    )

  return (
    <div className='bg-white rounded-2 flex flex-col pb-6 w-full'>
      <div className='flex w-full justify-between'>
        <h3 className='font-[500] leading-[150%] pb-4 pl-1 m-0 text-[14px]'>Rolling Returns (p.a.)</h3>
        <HelpButton
          aria-label='Help about rolling returns'
          onClick={() => {
            setBottomSheetContentKey('rrContent')
            setOpenBottomSheet((open) => !open)
          }}
        />
      </div>
      <div className='w-full flex gap-3'>
        <div className='flex ' style={{ width: 'calc(50% - 6px)' }}>
          <ToggleBox
            isToggleEnabled={false}
            toggleBoxClassName='bg-[#3c62da1a]'
            titleClassName='text-[#004D99]'
            toggleOptions={['This fund']}
            currentToggleOption='This fund'
            onToggleClick={() => null}
          />{' '}
        </div>
        <div className='flex' style={{ width: 'calc(50% - 6px)' }}>
          <ToggleBox
            isToggleEnabled={true}
            toggleBoxClassName='bg-[#FAEFE3]'
            titleClassName='text-[#992E00]'
            toggleColor='#992E00'
            toggleOptions={metadata?.benchmarkFunds?.map((benchmark) => benchmark?.name) ?? []}
            currentToggleOption={currentBenchmarktoggle?.name}
            onToggleClick={(name: string) => {
              const filteredBenchmark = metadata?.benchmarkFunds?.filter((benchmark) => benchmark?.name === name)?.[0]
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: 'benchmarks',
                  responseName: name,
                  ScreenName: `MF_L2_Rolling_Returns_Performance`,
                  fundName: isin,
                  screenType: 'MF_L2'
                }
              })
              setCurrentBenchmarkToggle({
                name: name,
                type: filteredBenchmark?.type ?? '',
                value: filteredBenchmark?.value ?? ''
              })
            }}
          />{' '}
        </div>
      </div>
      {isError || data1?.length <= 0 || data2?.length <= 0 ? (
        <div className='flex flex-col w-full py-2'>
          <div className='w-full h-[275px] py-5'>
            <GraphUnavailable />
          </div>
          <div className='text-13 text-[#6A6E6E]'>
            Graph data is unavailable as this fund is newly launched. Please check back later for updates.
          </div>
        </div>
      ) : (
        <div className='h-[335px] w-full'>{data1 && data2 && <GroupedBarChart data1={data1} data2={data2} />}</div>
      )}
      <div className='pt-6'>
        <TextBubble
          text='Rolling returns is a better metric to track a mutual fund’s long term performance.'
          learnMoreText='See why'
          onLearnMore={() => {
            lightImpactVibrate()
            setBottomSheetContentKey('learnMore')
            setOpenBottomSheet((open) => !open)
          }}
        />
      </div>
      <VaulDrawer open={openBottomSheet} setOpen={setOpenBottomSheet}>
        {renderBottomSheetContent()}
      </VaulDrawer>
    </div>
  )
}
